import { c as _c } from "react/compiler-runtime";
import { cx } from 'class-variance-authority';
import type * as CSS from 'csstype';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import type { createElementProps } from 'react-syntax-highlighter';
import createElement from 'react-syntax-highlighter/dist/cjs/create-element';
import CopyButton from '../copy-button';
import { resolveLanguage, SyntaxHighlighter } from './languages';
type RendererNode = createElementProps['node'];
type RendererProps = {
  rows: RendererNode[];
  stylesheet: createElementProps['stylesheet'];
  useInlineStyles: boolean;
};
type CodeBlockToken = {
  text: string;
  component: React.ComponentType<{
    children?: React.ReactNode;
  }>;
};
type CodeBlockProps = {
  ariaLabel?: string;
  children: string;
  // Copied to the clipboard instead of `children` — e.g. a revealed secret while the block displays
  // a scrubbed one.
  contentToCopy?: string;
  disableCopy?: boolean;
  highlightedLines?: number[];
  language: string;
  showLineNumbers?: boolean;
  title?: string | React.ReactNode;
  tooltip?: {
    position?: 'top' | 'bottom' | 'left' | 'right';
    text?: string;
    textConfirmation?: string;
  };
  isPrivate?: boolean;
  backgroundColor?: 'primary' | 'secondary';
  hideHeader?: boolean;
  // Caps the code body's height; it scrolls internally so the header and border stay fixed.
  maxHeight?: CSS.Property.MaxHeight;
  // Renders `component` in place of every occurrence of `text`, e.g. to make a key in the code interactive.
  inlineTokens?: CodeBlockToken[];
};
const textNode = (value: string): RendererNode => ({
  type: 'text',
  value
});
const splitTextNode = (node: RendererNode, tokens: CodeBlockToken[]): RendererNode[] => {
  const value = String(node.value ?? '');
  const token = tokens.find(candidate => value.includes(candidate.text));
  if (!token) {
    return [node];
  }
  const start = value.indexOf(token.text);
  const before = value.slice(0, start);
  const after = value.slice(start + token.text.length);
  const tokenNode: RendererNode = {
    type: 'element',
    tagName: token.component,
    properties: {
      className: []
    },
    children: [textNode(token.text)]
  };
  return [...(before ? [textNode(before)] : []), tokenNode, ...(after ? splitTextNode(textNode(after), tokens) : [])];
};
const injectTokens = (node: RendererNode, tokens: CodeBlockToken[]): RendererNode => ({
  ...node,
  children: node.children?.flatMap(child => child.type === 'text' ? splitTextNode(child, tokens) : [injectTokens(child, tokens)])
});
const CodeBlock = t0 => {
  const $ = _c(44);
  const {
    ariaLabel,
    children,
    contentToCopy,
    disableCopy,
    highlightedLines: t1,
    language,
    showLineNumbers,
    title,
    tooltip,
    isPrivate,
    backgroundColor,
    hideHeader,
    maxHeight,
    inlineTokens
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const highlightedLines = t2;
  const {
    t
  } = useTranslation("ui");
  let t3;
  if ($[2] !== inlineTokens) {
    t3 = inlineTokens?.length ? t4 => {
      const {
        rows,
        stylesheet,
        useInlineStyles
      } = t4;
      return rows.map((row, index) => createElement({
        node: injectTokens(row, inlineTokens),
        stylesheet,
        useInlineStyles,
        key: `code-segment${index}`
      }));
    } : undefined;
    $[2] = inlineTokens;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const renderWithTokens = t3;
  const t4 = backgroundColor === "secondary";
  let t5;
  if ($[4] !== t4) {
    t5 = cx("flex flex-col overflow-hidden border border-solid border-tertiary rounded bg-primary", {
      "bg-secondary": t4
    });
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const t6 = isPrivate ? "mask" : undefined;
  let t7;
  if ($[6] !== ariaLabel || $[7] !== children || $[8] !== contentToCopy || $[9] !== disableCopy || $[10] !== hideHeader || $[11] !== language || $[12] !== t || $[13] !== title || $[14] !== tooltip?.position || $[15] !== tooltip?.text || $[16] !== tooltip?.textConfirmation) {
    t7 = !hideHeader && <div className="flex items-center justify-between w-full pl-4 pr-2 border-b border-solid h-9 border-tertiary bg-secondary"><div className="flex items-center justify-center max-w-full gap-3 overflow-hidden text-label-2 text-secondary">{title || language}</div>{disableCopy ? null : <CopyButton ariaLabel={ariaLabel ?? t("components.code-block.aria-label-default") as string} contentToCopy={contentToCopy ?? children} size="compact" tooltip={{
        position: tooltip?.position ?? "top",
        text: tooltip?.text ?? t("components.code-block.tooltip-text-default") as string,
        textConfirmation: tooltip?.textConfirmation ?? t("components.code-inline.tooltip-text-confirmation-default") as string
      }} />}</div>;
    $[6] = ariaLabel;
    $[7] = children;
    $[8] = contentToCopy;
    $[9] = disableCopy;
    $[10] = hideHeader;
    $[11] = language;
    $[12] = t;
    $[13] = title;
    $[14] = tooltip?.position;
    $[15] = tooltip?.text;
    $[16] = tooltip?.textConfirmation;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const t8 = maxHeight && "overflow-y-auto";
  let t9;
  if ($[18] !== t8) {
    t9 = cx("relative justify-center w-full align-middle text-snippet-2", t8);
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== maxHeight) {
    t10 = maxHeight ? {
      maxHeight
    } : undefined;
    $[20] = maxHeight;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== language) {
    t11 = resolveLanguage(language);
    $[22] = language;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      background: "primary",
      fontFamily: "var(--font-family-code)",
      padding: 0,
      margin: 0,
      maxWidth: "100%",
      overflowX: "auto",
      display: "block"
    };
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      color: "var(--text-color-tertiary)",
      fontFamily: "var(--font-family-code)",
      WebkitUserSelect: "none",
      width: "2ch",
      paddingRight: "0px",
      textAlign: "left",
      marginRight: "0.5rem"
    };
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== highlightedLines) {
    t14 = lineNumber => {
      const isHighlighted = highlightedLines.includes(lineNumber);
      return {
        className: cx("block relative isolate min-w-full", {
          "before:absolute before:content-[\"\"] before:-left-4 before:-right-6 before:top-0 before:bottom-0 before:bg-warning before:z-[-1]": isHighlighted
        }),
        style: isHighlighted ? {
          position: "relative"
        } : undefined
      };
    };
    $[26] = highlightedLines;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      "hljs-comment": {
        color: "var(--hljs-comment-color)",
        fontStyle: "var(--hljs-comment-font-style)"
      },
      "hljs-doctag": {
        color: "var(--hljs-doctag-color)"
      },
      "hljs-keyword": {
        color: "var(--hljs-keyword-color)"
      },
      "hljs-formula": {
        color: "var(--hljs-formula-color)"
      },
      "hljs-section": {
        color: "var(--hljs-section-color)"
      },
      "hljs-name": {
        color: "var(--hljs-name-color)"
      },
      "hljs-selector-tag": {
        color: "var(--hljs-selector-tag-color)"
      },
      "hljs-deletion": {
        color: "var(--hljs-deletion-color)"
      },
      "hljs-subst": {
        color: "var(--hljs-subst-color)"
      },
      "hljs-literal": {
        color: "var(--hljs-literal-color)"
      },
      "hljs-string": {
        color: "var(--hljs-string-color)"
      },
      "hljs-regexp": {
        color: "var(--hljs-regexp-color)"
      },
      "hljs-addition": {
        color: "var(--hljs-addition-color)"
      },
      "hljs-attribute": {
        color: "var(--hljs-attribute-color)"
      },
      "hljs-meta-string": {
        color: "var(--hljs-meta-string-color)"
      },
      "hljs-built_in": {
        color: "var(--hljs-built-in-color)"
      },
      "hljs-class .hljs-title": {
        color: "var(--hljs-class-title-color)"
      },
      "hljs-attr": {
        color: "var(--hljs-attr-color)"
      },
      "hljs-variable": {
        color: "var(--hljs-variable-color)"
      },
      "hljs-template-variable": {
        color: "var(--hljs-template-variable-color)"
      },
      "hljs-type": {
        color: "var(--hljs-type-color)"
      },
      "hljs-selector-class": {
        color: "var(--hljs-selector-class-color)"
      },
      "hljs-selector-attr": {
        color: "var(--hljs-selector-attr-color)"
      },
      "hljs-selector-pseudo": {
        color: "var(--hljs-selector-pseudo-color)"
      },
      "hljs-number": {
        color: "var(--hljs-number-color)"
      },
      "hljs-symbol": {
        color: "var(--hljs-symbol-color)"
      },
      "hljs-bullet": {
        color: "var(--hljs-bullet-color)"
      },
      "hljs-link": {
        color: "var(--hljs-link-color)",
        textDecoration: "var(--hljs-link-text-decoration)"
      },
      "hljs-meta": {
        color: "var(--hljs-meta-color)"
      },
      "hljs-selector-id": {
        color: "var(--hljs-selector-id-color)"
      },
      "hljs-title": {
        color: "var(--hljs-title-color)"
      },
      "hljs-emphasis": {
        fontStyle: "var(--hljs-emphasis-font-style)"
      },
      "hljs-strong": {
        fontWeight: "var(--hljs-strong-font-weight)"
      }
    };
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== children || $[30] !== renderWithTokens || $[31] !== showLineNumbers || $[32] !== t11 || $[33] !== t14) {
    t16 = <div className="[&>pre]:!m-0 [&>pre]:!bg-transparent [&>pre]:!p-4 [&>pre>code>span]:!leading-5 [&>pre>code]:!inline-block [&>pre>code]:!min-w-full [&>pre>code>span>.hljs-comment]:!whitespace-normal [&>pre>code>span>.comment]:!whitespace-normal [&>pre>code>span>.hljs-comment]:!break-words [&>pre>code>span>.comment]:!break-words"><SyntaxHighlighter language={t11} renderer={renderWithTokens} showLineNumbers={showLineNumbers} customStyle={t12} wrapLines={true} lineNumberStyle={t13} lineProps={t14} style={t15}>{children}</SyntaxHighlighter></div>;
    $[29] = children;
    $[30] = renderWithTokens;
    $[31] = showLineNumbers;
    $[32] = t11;
    $[33] = t14;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t10 || $[36] !== t16 || $[37] !== t9) {
    t17 = <div className={t9} style={t10}>{t16}</div>;
    $[35] = t10;
    $[36] = t16;
    $[37] = t9;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t17 || $[40] !== t5 || $[41] !== t6 || $[42] !== t7) {
    t18 = <div className={t5} data-dd-privacy={t6}>{t7}{t17}</div>;
    $[39] = t17;
    $[40] = t5;
    $[41] = t6;
    $[42] = t7;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  return t18;
};
export type { CodeBlockProps, CodeBlockToken };
export default CodeBlock;